import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import __ from 'utils/i18n';
import { Input } from 'reactstrap';

/**
 * Sanitize input: optionally filter characters when customFilter is provided, apply max length
 */
const sanitizeInput = (input, filterFn, maxLen) => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  let sanitized = input;

  // Apply custom filter only when provided
  if (filterFn && typeof filterFn === 'function') {
    sanitized = sanitized
      .split('')
      .filter(char => filterFn(char))
      .join('');
  }

  // Apply max length limit if specified
  if (maxLen !== null && maxLen > 0 && sanitized.length > maxLen) {
    sanitized = sanitized.substring(0, maxLen);
  }

  return sanitized;
};

/**
 * Check if a character is allowed (all allowed when no customFilter)
 */
const isCharacterAllowed = (char, filterFn) => {
  if (!filterFn || typeof filterFn !== 'function') {
    return true;
  }
  return filterFn(char);
};

/**
 * ServerSideSearch component for API-based search
 * This component handles search input that will be sent to the server via query parameters
 * 
 * Features:
 * - Minimum characters required before triggering search (configurable)
 * - Maximum characters limit (configurable)
 * - Optional custom filter function support (no filtering by default)
 * - When customFilter is provided, prevents typing of filtered characters
 * 
 * @param {string} value - Current search text value
 * @param {function} onChange - Callback function called when filtered value changes AND meets minLength (or empty)
 * @param {string} placeholder - Placeholder text for the input (optional)
 * @param {number} minLength - Minimum characters required to trigger search (default: 0)
 * @param {number} maxLength - Maximum characters allowed (default: null, no limit)
 * @param {function} customFilter - Custom filter function (char) => boolean to determine allowed characters
 * @param {number} delay - Debounce delay in ms before triggering onChange (default: 300). Use 0 for immediate emit.
 */
const ServerSideSearch = ({ 
  value, 
  onChange, 
  placeholder, 
  minLength = 0, 
  maxLength = null,
  customFilter = null,
  delay = 500
}) => {
  const [internalValue, setInternalValue] = useState(value || '');
  const lastEmittedValue = useRef(value || '');
  const debounceTimerRef = useRef(null);

  // Sync internal value with external value prop
  useEffect(() => {
    const sanitized = sanitizeInput(value || '', customFilter, maxLength);
    setInternalValue(sanitized);
    lastEmittedValue.current = sanitized;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, [value, customFilter, maxLength]);

  // Debounce: when internalValue changes and meets criteria, schedule onChange after delay
  useEffect(() => {
    const shouldEmit = internalValue.length === 0 || internalValue.length >= minLength;
    const hasChanged = internalValue !== lastEmittedValue.current;

    if (!shouldEmit || !hasChanged) {
      return;
    }

    if (delay === 0) {
      lastEmittedValue.current = internalValue;
      onChange && onChange(internalValue);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      lastEmittedValue.current = internalValue;
      onChange && onChange(internalValue);
      debounceTimerRef.current = null;
    }, delay);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [internalValue, minLength, delay, onChange]);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    // Sanitize input: filter characters and apply max length
    const filteredValue = sanitizeInput(inputValue, customFilter, maxLength);
    
    // Update internal value (always update to show filtered result)
    // onChange is triggered via debounce effect when criteria are met
    setInternalValue(filteredValue);
  };

  const handleKeyPress = (e) => {
    // Prevent typing invalid characters
    const char = e.key;
    
    // Allow control keys (backspace, delete, arrow keys, etc.)
    if (e.ctrlKey || e.metaKey || e.altKey || 
        char.length > 1 || // Special keys like 'Backspace', 'Delete', etc.
        char === '\b' || char === '\t' || char === '\r' || char === '\n') {
      return;
    }
    
    // Check if character is allowed
    if (!isCharacterAllowed(char, customFilter)) {
      e.preventDefault();
    }
    
    // Check max length
    if (maxLength !== null && maxLength > 0 && internalValue.length >= maxLength) {
      e.preventDefault();
    }
  };

  return (
    <div className="mr-auto">
      <div className="input-group has-feedback full-search">
        <Input
          type="text"
          className="form-control"
          placeholder={placeholder || __('dashboard.components.oldFilterForm.fields.search.placeholder')}
          value={internalValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          maxLength={maxLength || undefined}
        />
        <div className="input-group-feedback">
          <i className="mdi mdi-magnify"/>
        </div>
      </div>
      {minLength > 0 && internalValue.length > 0 && internalValue.length < minLength && (
        <small className="text-muted d-block mt-1">
          {__('dashboard.components.serverSideSearch.minLengthError', { min: minLength }) ||
            `min: ${minLength} letras`}
        </small>
      )}
      {maxLength && internalValue.length >= maxLength && (
        <small className="text-muted d-block mt-1">
          {__('dashboard.components.serverSideSearch.maxLength', { max: maxLength }) || 
            `Máximo ${maxLength} letras`}
        </small>
      )}
    </div>
  );
};

ServerSideSearch.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  customFilter: PropTypes.func,
  delay: PropTypes.number,
};

export default ServerSideSearch;
